import { useCallback, useState } from 'react'
import { Cpu, Box } from 'lucide-react'
import AdminScreenShell, { Tegel, StatusRij } from '@/components/AdminScreenShell'
import { adminAPI } from '@/lib/api'

/* Toont ruwe system-health cijfers groot en leesbaar. */
export default function AdminSystemScreen() {
  const [data, setData] = useState(null)
  const [err, setErr]   = useState(null)

  const load = useCallback(async () => {
    try { const { data } = await adminAPI.getSystemHealth(); setData(data); setErr(null) }
    catch (e) { setErr(e.response?.data?.error ?? e.message) }
  }, [])

  const containers = Array.isArray(data?.docker) ? data.docker : []
  const disks      = Array.isArray(data?.disk)   ? data.disk   : []
  const cpu        = data?.cpu ?? data?.system?.cpu
  const mem        = data?.memory ?? data?.system?.memory
  const pg         = data?.postgres

  const memPct = mem?.used && mem?.total ? Math.round(mem.used / mem.total * 100) : null

  return (
    <AdminScreenShell title="Systeem" subtitle="LAN-server & backend gezondheid"
                      icon={Cpu} accent="#00d9ff" onRefresh={load} refreshMs={10_000}>
      {err && <div className="text-signal text-sm mb-4">{err}</div>}

      <div className="grid gap-4 grid-cols-2 md:grid-cols-4 mb-6">
        <Tegel label="CPU" waarde={cpu?.load ? `${cpu.load}%` : (cpu?.usage ? `${cpu.usage}%` : '—')}
               sub={cpu?.cores ? `${cpu.cores} cores` : ''} kleur="#00d9ff" />
        <Tegel label="Geheugen" waarde={memPct != null ? `${memPct}%` : '—'}
               sub={mem ? `${humanBytes(mem.used)} / ${humanBytes(mem.total)}` : ''} kleur="#B4FF39" />
        <Tegel label="Postgres" waarde={pg?.status === 'online' ? 'online' : 'off'}
               sub={pg ? `${pg.active_connections} verb · ${pg.db_size}` : ''}
               kleur={pg?.status === 'online' ? '#61E294' : '#FF5A5A'} />
        <Tegel label="Containers" waarde={containers.filter(c => c.healthy).length + '/' + containers.length}
               sub="healthy / totaal" kleur="#FFB57A" />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <section>
          <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
            <Box className="inline h-3 w-3 mr-1" /> Docker · {containers.length}
          </h2>
          <div className="rounded-[6px] border border-border bg-card px-4 max-h-[52vh] overflow-y-auto">
            {containers.length === 0 && <p className="py-3 text-sm text-muted-foreground">Geen containers zichtbaar.</p>}
            {containers.map(c => (
              <StatusRij key={c.name} naam={c.name} sub={c.status}
                         status={c.healthy ? 'ok' : 'down'}
                         kleur={c.healthy ? '#61E294' : '#FF5A5A'} />
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
            Disks · {disks.length}
          </h2>
          <div className="rounded-[6px] border border-border bg-card px-4">
            {disks.length === 0 && <p className="py-3 text-sm text-muted-foreground">Geen data.</p>}
            {disks.map(d => {
              const pct = d.total ? Math.round((d.used / d.total) * 100) : 0
              const kleur = pct >= 90 ? '#FF5A5A' : pct >= 75 ? '#FFB57A' : '#61E294'
              return (
                <StatusRij key={d.mount || d.filesystem} naam={d.mount || d.filesystem}
                           sub={`${humanBytes(d.used)} / ${humanBytes(d.total)}`}
                           status={`${pct}%`} kleur={kleur} />
              )
            })}
          </div>
        </section>
      </div>
    </AdminScreenShell>
  )
}

function humanBytes(n) {
  if (!n || Number.isNaN(Number(n))) return '—'
  const units = ['B', 'K', 'M', 'G', 'T']
  let v = Number(n), i = 0
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${v.toFixed(v < 10 ? 1 : 0)}${units[i]}`
}
